import React, { useState, useEffect } from 'react';
import { ErrorsWrapper, InputLabel } from 'ui-builder';
import { ColorPickerWithTextLayoutProps } from '../types';
import { HexColorPicker } from 'react-colorful';
import { useLayer } from 'react-laag';

import styles from './ColorPickerWithTextLayout.module.scss';

export default function ColorPickerWithTextLayoutWeb(props: ColorPickerWithTextLayoutProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [colorValue, setColorValue] = useState(props.value || props.defaultColor || '#426377');

  useEffect(() => {
    setColorValue(props.value || props.defaultColor || '#426377');
  }, [props.value, props.defaultColor]);

  const { renderLayer, triggerProps, layerProps } = useLayer({
    isOpen,
    onOutsideClick: () => setIsOpen(false),
    onDisappear: () => setIsOpen(false),
    overflowContainer: true,
    auto: true,
    placement: 'bottom-start',
    triggerOffset: 8,
  });

  // Extract RGB and alpha from hex color
  const getAlphaFromHex = (hex: string): number => {
    if (hex.length === 9) {
      return parseInt(hex.slice(7, 9), 16);
    }
    return 255; // Default to fully opaque
  };

  const getRgbFromHex = (hex: string): string => {
    return hex.slice(0, 7); // Get #RRGGBB part
  };

  const hexToOpacity = (alpha: number): number => {
    return Math.round((alpha / 255) * 100);
  };

  const opacityToHex = (opacity: number): string => {
    const alpha = Math.round((opacity / 100) * 255);
    return alpha.toString(16).padStart(2, '0');
  };

  const handleColorChange = (newColor: string) => {
    // Preserve alpha when changing color from picker
    const currentAlpha = getAlphaFromHex(colorValue);
    const newColorWithAlpha = newColor.length === 9
      ? newColor
      : `${getRgbFromHex(newColor)}${currentAlpha.toString(16).padStart(2, '0')}`;
    setColorValue(newColorWithAlpha);
    props.onChangeCallback({ target: { value: newColorWithAlpha } });
  };

  const handleOpacityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const opacity = parseInt(e.target.value, 10);
    const rgb = getRgbFromHex(colorValue);
    const newColor = `${rgb}${opacityToHex(opacity)}`;
    setColorValue(newColor);
    props.onChangeCallback({ target: { value: newColor } });
  };

  const handleTextInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (props.isDisabled) {
      return;
    }
    const newValue = e.target.value;
    setColorValue(newValue);
    props.onChangeCallback({ target: { value: newValue } });
  };

  const displayColor = colorValue || props.defaultColor || '#426377';
  const currentOpacity = hexToOpacity(getAlphaFromHex(displayColor));

  return (
    <ErrorsWrapper name={props.name} errors={props.errors}>
      <InputLabel source={props.name} label={props.label} hideLabel={props.hideLabel} />

      <div className={styles.container}>
        <button
          type="button"
          className={styles.colorSquare}
          style={{ backgroundColor: displayColor }}
          {...triggerProps}
          onClick={() => !props.isDisabled && setIsOpen(!isOpen)}
          title={props.isDisabled ? "This color is locked" : "Click to open color picker"}
          disabled={props.isDisabled}
        />

        <input
          type="text"
          className={styles.textInput}
          value={colorValue}
          onChange={handleTextInputChange}
          placeholder="#426377"
          maxLength={9}
          pattern="^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$"
          disabled={props.isDisabled}
          readOnly={props.isDisabled}
        />
      </div>

      {props.hint && <div className={styles.hint}>{props.hint}</div>}

      {isOpen && !props.isDisabled && renderLayer(
        <div className={styles.overlay} {...layerProps}>
          <div className={styles.pickerContainer}>
            <HexColorPicker
              color={displayColor}
              onChange={handleColorChange}
            />

            <div className={styles.opacitySlider}>
              <label className={styles.opacityLabel}>
                Opacity: {currentOpacity}%
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={currentOpacity}
                onChange={handleOpacityChange}
                className={styles.slider}
              />
            </div>

            <div className={styles.presetColors}>
              <div className={styles.presetLabel}>Quick colors:</div>
              <div className={styles.presetGrid}>
                {['#426377', '#FFFFFF', '#4cd964', '#eefcf0', '#000000', '#FF0000', '#00FF00', '#0000FF'].map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={styles.presetSwatch}
                    style={{ backgroundColor: color }}
                    onClick={() => handleColorChange(color)}
                    title={color}
                  />
                ))}
              </div>
            </div>

            <button
              type="button"
              className={styles.closeButton}
              onClick={() => setIsOpen(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </ErrorsWrapper>
  );
}
